import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  House, Tag, Calendar, MagnifyingGlass, X,
  Broom, PaperPlaneTilt, DotsThreeVertical,
} from "@phosphor-icons/react";

const STATUSES = [
  { key: "occupied",       label: "Occupied",       pill: "bg-slate-100 text-slate-700 border-slate-300",       stripe: "bg-slate-300" },
  { key: "notice_given",   label: "Notice Given",   pill: "bg-amber-100 text-amber-900 border-amber-400",       stripe: "bg-amber-400" },
  { key: "vacating_soon",  label: "Vacating Soon",  pill: "bg-orange-100 text-orange-900 border-orange-500",    stripe: "bg-orange-500" },
  { key: "vacant",         label: "Vacant",         pill: "bg-red-100 text-red-900 border-red-500",             stripe: "bg-red-500" },
  { key: "make_ready",     label: "Make-Ready",     pill: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-500", stripe: "bg-fuchsia-500" },
];
const SUGGESTED_TAGS = ["section8", "pet_friendly", "needs_paint", "needs_carpet", "furnished", "no_pets"];

export default function VacancyRadar() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    try {
      const params = filter && filter !== "all" ? { status: filter } : {};
      const r = await api.get("/vacancies", { params });
      setRows(r.data.properties || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      if (e.response?.status === 402)
        toast.error("Vacancy Radar requires the Management module — upgrade in Billing.");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const visible = (rows || []).filter(p => !q.trim() || (p.address || "").toLowerCase().includes(q.toLowerCase()));

  const setStatus = async (p, newStatus) => {
    try {
      await api.patch(`/property-configs/${p.id}/vacancy`, { vacancy_status: newStatus });
      toast.success(`${p.address} → ${STATUSES.find(s => s.key === newStatus)?.label || newStatus}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
  };

  const sendTurnKit = async (p) => {
    if (!window.confirm(`Create a Turn work order for ${p.address}${p.unit ? " · Unit " + p.unit : ""}?\n\nThis seeds the master turn checklist (paint, clean, carpet, HVAC filter, re-key, etc.) and marks the property as Make-Ready.`)) return;
    setBusyId(p.id);
    try {
      const payload = {
        title: `Turn — ${p.address}${p.unit ? " · Unit " + p.unit : ""}`,
        description: "Auto-created from Vacancy Radar. Full turn checklist attached.",
        property_address: p.address || "",
        unit: p.unit || "",
        priority: "high",
        is_turn: true,
        checklist: [],  // backend seeds TURN_CHECKLIST_TEMPLATE
        room_config: p.room_config || undefined,
      };
      const { data: job } = await api.post("/jobs", payload);
      // Flip vacancy → make_ready so the door leaves the "vacant" bucket.
      try {
        await api.patch(`/property-configs/${p.id}/vacancy`, { vacancy_status: "make_ready" });
      } catch {}
      toast.success("Turn kit created — opening the job");
      nav(`/admin/jobs/${job.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create Turn");
    } finally { setBusyId(""); }
  };

  const listOnProspectRadar = async (p) => {
    setBusyId(p.id);
    try {
      await api.post("/prospects", {
        name: `Listing · ${p.address}${p.unit ? " · Unit " + p.unit : ""}`,
        property_address: p.address || "",
        unit: p.unit || "",
        source: "vacancy_radar",
        notes: `Auto-listed from Vacancy Radar. Property vacates ${p.expected_vacate_date || "soon"}. Tags: ${(p.tags || []).join(", ") || "—"}.`,
      });
      toast.success("Placeholder listing added to Prospect Radar");
      nav("/admin/prospects");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create listing");
    } finally { setBusyId(""); }
  };

  const totalDoors = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
  const openDoors = (counts.notice_given || 0) + (counts.vacating_soon || 0) + (counts.vacant || 0) + (counts.make_ready || 0);
  const openPct = totalDoors > 0 ? Math.round((openDoors / totalDoors) * 100) : 0;

  return (
    <AdminShell>
      <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="vacancy-radar-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center border-2 border-[#0f172a]">
            <House weight="fill" size={22} className="text-white"/>
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Vacancy Radar™</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Track which doors are occupied, on notice, or vacant. One tap dispatches a full Turn kit or lists the door on Prospect Radar for re-lease.
        </p>

        {/* KPI STRIP */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="vacancy-kpis">
          <KpiCard label="Portfolio" value={totalDoors} color="text-slate-900" hint={`${openPct}% currently open`}/>
          <KpiCard label="Notice Given" value={counts.notice_given || 0} color="text-amber-700"/>
          <KpiCard label="Vacating Soon" value={counts.vacating_soon || 0} color="text-orange-700" pulse={((counts.vacating_soon || 0) > 0)}/>
          <KpiCard label="Vacant" value={counts.vacant || 0} color="text-red-700" pulse={((counts.vacant || 0) > 0)}/>
          <KpiCard label="Make-Ready" value={counts.make_ready || 0} color="text-fuchsia-700"/>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="vacancy-filters">
          {[
            { k: "open", label: `Open · ${openDoors}` },
            { k: "leaving_soon", label: `Leaving Soon · ${(counts.notice_given || 0) + (counts.vacating_soon || 0)}` },
            { k: "vacant", label: `Vacant · ${counts.vacant || 0}` },
            { k: "make_ready", label: `Make-Ready · ${counts.make_ready || 0}` },
            { k: "occupied", label: `Occupied · ${counts.occupied || 0}` },
            { k: "all", label: "All Properties" },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`vacancy-filter-${f.k}`}
              className={`h-9 px-3 text-[10px] font-black uppercase tracking-widest border-2 transition ${
                filter === f.k ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300 hover:bg-slate-50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-3 relative max-w-sm">
          <MagnifyingGlass weight="bold" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by address…"
            data-testid="vacancy-search"
            className="w-full h-10 pl-8 pr-3 border-2 border-[#0f172a] text-sm font-medium"/>
        </div>

        <div className="mt-4 space-y-2" data-testid="vacancy-list">
          {rows === null && <div className="text-center text-slate-400 font-black uppercase tracking-widest text-xs py-6">Loading…</div>}
          {rows && visible.length === 0 && (
            <div className="rugged-card p-8 text-center bg-white" data-testid="vacancy-empty">
              <House size={40} className="mx-auto text-slate-300" weight="duotone"/>
              <div className="mt-2 font-display font-black text-lg uppercase tracking-tight">No properties match this filter</div>
              <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                Change the status filter above, or head to <b>Properties</b> to add your first door — vacancy tracking populates automatically once you tag a notice date.
              </p>
            </div>
          )}
          {visible.map(p => {
            const st = STATUSES.find(s => s.key === p.vacancy_status) || STATUSES[0];
            const showTurnKit = ["vacant", "make_ready"].includes(p.vacancy_status);
            const showListing = ["notice_given", "vacating_soon", "vacant"].includes(p.vacancy_status);
            return (
              <div key={p.id} data-testid={`vacancy-row-${p.id}`}
                className="relative bg-white border-2 border-[#0f172a] shadow-[3px_3px_0px_#0f172a] hover:shadow-[5px_5px_0px_#00C4CC] transition-shadow overflow-hidden">
                <div className={`absolute inset-y-0 left-0 w-1.5 ${st.stripe}`} aria-hidden />
                <div className="pl-4 pr-3 py-3 flex items-center gap-3 flex-wrap">
                  <House size={16} className="text-slate-400 flex-shrink-0"/>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-black text-sm">
                      {p.address}
                      {p.unit && <span className="text-slate-500 font-normal"> · Unit {p.unit}</span>}
                    </div>
                    {p.expected_vacate_date && (
                      <div className="text-[11px] font-bold text-orange-600">
                        <Calendar weight="bold" size={11} className="inline mr-0.5"/> Vacates {p.expected_vacate_date}
                      </div>
                    )}
                    {(p.tags || []).length > 0 && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {(p.tags || []).map(t => (
                          <span key={t} className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 border border-cyan-500 text-cyan-900 px-1.5 py-0.5">
                            <Tag weight="bold" size={8} className="inline mr-0.5"/>{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-1 border-2 text-[10px] font-black uppercase tracking-widest ${st.pill}`} data-testid={`vacancy-status-${p.id}`}>
                    {st.label}
                  </span>
                  <select
                    value={p.vacancy_status || "occupied"}
                    onChange={e => setStatus(p, e.target.value)}
                    data-testid={`vacancy-status-select-${p.id}`}
                    className="h-9 px-2 border-2 border-[#0f172a] text-[11px] font-medium bg-white"
                  >
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  {showTurnKit && (
                    <button onClick={() => sendTurnKit(p)} disabled={busyId === p.id} data-testid={`vacancy-turn-kit-${p.id}`}
                      className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-60">
                      <Broom weight="fill" size={12}/> Send Turn Kit
                    </button>
                  )}
                  {showListing && (
                    <button onClick={() => listOnProspectRadar(p)} disabled={busyId === p.id} data-testid={`vacancy-list-prospect-${p.id}`}
                      className="h-9 px-3 bg-[#00C4CC] hover:bg-cyan-500 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-60">
                      <PaperPlaneTilt weight="fill" size={12}/> List on Prospect Radar
                    </button>
                  )}
                  <button onClick={() => setEditing(p)} data-testid={`vacancy-edit-${p.id}`}
                    className="h-9 px-3 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 inline-flex items-center gap-1">
                    <DotsThreeVertical weight="bold" size={12}/> Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {editing && <VacancyDetailModal p={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function KpiCard({ label, value, color, hint, pulse }) {
  return (
    <div className={`rugged-card p-3 bg-white ${pulse ? "animate-pulse-slow" : ""}`}>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display font-black text-2xl ${color}`}>{value}</div>
      {hint && <div className="text-[10px] font-bold text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function VacancyDetailModal({ p, onClose, onSaved }) {
  const [f, setF] = useState({
    vacancy_status: p.vacancy_status || "occupied",
    notice_date: p.notice_date || "",
    expected_vacate_date: p.expected_vacate_date || "",
    tags: (p.tags || []).join(", "),
    vacancy_note: p.vacancy_note || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/property-configs/${p.id}/vacancy`, {
        ...f,
        tags: f.tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      toast.success("Vacancy updated");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const inputCls = "w-full h-10 px-3 border-2 border-slate-300 text-sm font-medium focus:border-[#0f172a] focus:outline-none";
  const toggleTag = (t) => {
    const arr = f.tags.split(",").map(x => x.trim()).filter(Boolean);
    const has = arr.includes(t);
    const next = has ? arr.filter(x => x !== t) : [...arr, t];
    setF({ ...f, tags: next.join(", ") });
  };
  const activeTags = f.tags.split(",").map(x => x.trim()).filter(Boolean);
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] w-full max-w-md" onClick={e => e.stopPropagation()}
        data-testid="vacancy-detail-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a]">
          <div>
            <div className="font-display font-black uppercase text-lg">{p.address}</div>
            {p.unit && <div className="text-xs text-slate-500">Unit {p.unit}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="vacancy-detail-close" className="p-2 hover:bg-slate-100"><X weight="bold" size={16}/></button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Status</label>
            <select value={f.vacancy_status} onChange={e => setF({...f, vacancy_status: e.target.value})} data-testid="vacancy-detail-status"
              className={inputCls}>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Notice Given</label>
              <input type="date" value={f.notice_date} onChange={e => setF({...f, notice_date: e.target.value})} data-testid="vacancy-detail-notice" className={inputCls}/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Expected Vacate</label>
              <input type="date" value={f.expected_vacate_date} onChange={e => setF({...f, expected_vacate_date: e.target.value})} data-testid="vacancy-detail-vacate" className={inputCls}/>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Tags</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {SUGGESTED_TAGS.map(t => {
                const on = activeTags.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`vacancy-detail-tag-${t}`}
                    className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 border-2 ${
                      on ? "bg-cyan-500 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                    }`}>
                    {on ? "✓ " : "+ "}{t}
                  </button>
                );
              })}
            </div>
            <input value={f.tags} onChange={e => setF({...f, tags: e.target.value})} placeholder="Or type custom tags (comma-separated)" data-testid="vacancy-detail-tags"
              className={inputCls}/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Notes</label>
            <textarea rows={3} value={f.vacancy_note} onChange={e => setF({...f, vacancy_note: e.target.value})} data-testid="vacancy-detail-note"
              className="w-full px-3 py-2 border-2 border-slate-300 text-sm font-medium resize-none focus:border-[#0f172a] focus:outline-none"/>
          </div>
        </div>
        <div className="p-4 border-t-2 border-[#0f172a] flex items-center gap-2 justify-end">
          <button onClick={onClose} className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="vacancy-detail-save"
            className="h-11 px-5 bg-[#00C4CC] text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
